import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Info, ExternalLink, ChevronDown, CheckCircle2, Circle, RefreshCw, BookOpen, Edit3, Languages, Plane, Map as MapIcon, MapPinOff } from 'lucide-react';
import pilgrimageData from './data/pilgrimageData.json';
import AnimatedBackground from './components/AnimatedBackground';
import NotesFAB from './components/NotesFAB';
import MiniMap from './components/MiniMap';

// UI string table
const UI = {
  en: {
    title: "Umrah & Hajj Guide",
    travel: "travel",
    mecca: "mecca",
    medina: "medina",
    switchToHajj: "Switch to Hajj (حج)",
    switchToUmrah: "Switch to Umrah (عمرة)",
    moreInfo: "More Info, Context & Quran",
    hideContext: "Hide Detailed Context",
    personalNotes: "Personal Notes",
    personalNotesPlaceholder: "Add your personal notes for this step (e.g. hotel room number, names to make dua for, specific reminders)...",
    term: "Term:",
    sahih: "Sahih / Authentic",
    quranicCitation: "Quranic Citation (القرآن الكريم)",
    historicalFact: "Historical / Prophetic Fact",
    additionalSupplication: "Additional Supplication (دعاء إضافي)",
    sourceLabel: "Source:",
    languageToggleAr: "العربية",
    languageToggleEn: "English",
    audienceAll: "All",
    audienceMen: "Men",
    audienceWomen: "Women",
    forMen: "For men",
    forWomen: "For women",
    watermark: "Made by Rasool Mustafa"
  },
  ar: {
    title: "دليل العمرة والحج",
    travel: "السفر",
    mecca: "مكة",
    medina: "المدينة",
    switchToHajj: "التبديل إلى الحج",
    switchToUmrah: "التبديل إلى العمرة",
    moreInfo: "معلومات إضافية وسياق وآيات قرآنية",
    hideContext: "إخفاء التفاصيل",
    personalNotes: "ملاحظات شخصية",
    personalNotesPlaceholder: "أضف ملاحظاتك الشخصية لهذه الخطوة (مثل: رقم غرفة الفندق، أسماء للدعاء لهم، تذكيرات معينة)...",
    term: "مصطلح:",
    sahih: "صحيح",
    quranicCitation: "آية قرآنية",
    historicalFact: "حقيقة تاريخية / نبوية",
    additionalSupplication: "دعاء إضافي",
    sourceLabel: "المصدر:",
    languageToggleAr: "العربية",
    languageToggleEn: "English",
    audienceAll: "الكل",
    audienceMen: "للرجال",
    audienceWomen: "للنساء",
    forMen: "للرجال",
    forWomen: "للنساء",
    watermark: "إعداد رسول مصطفى"
  }
};

// Translation helper for content fields
const f = (obj, field, lang) => {
  if (!obj) return undefined;
  if (lang === 'ar' && obj[`${field}_ar`] !== undefined && obj[`${field}_ar`] !== null) {
    return obj[`${field}_ar`];
  }
  return obj[field];
};

// Normalize a bullet entry: strings become {text, audience:'all'}; objects pass through
const normalizeBullet = (b, lang) => {
  if (typeof b === 'string') return { text: b, audience: 'all' };
  return {
    text: lang === 'ar' && b.text_ar !== undefined ? b.text_ar : b.text,
    audience: b.audience || 'all'
  };
};

function App() {
  const [activeTab, setActiveTab] = useState('mecca');
  const [meccaMode, setMeccaMode] = useState('umrah');
  const [completedSteps, setCompletedSteps] = useState({});
  const [activeStepImage, setActiveStepImage] = useState(null);
  const [language, setLanguage] = useState('en');
  const [audience, setAudience] = useState('all'); // 'all' | 'men' | 'women'
  const [showMaps, setShowMaps] = useState(true);

  useEffect(() => {
    document.documentElement.classList.add('dark');

    const saved = localStorage.getItem('pilgrimageProgress');
    if (saved) setCompletedSteps(JSON.parse(saved));

    const savedLang = localStorage.getItem('language');
    if (savedLang === 'ar' || savedLang === 'en') setLanguage(savedLang);

    const savedAudience = localStorage.getItem('audience');
    if (['all', 'men', 'women'].includes(savedAudience)) setAudience(savedAudience);

    const savedMaps = localStorage.getItem('showMaps');
    if (savedMaps !== null) setShowMaps(savedMaps === 'true');
  }, []);

  useEffect(() => {
    document.documentElement.lang = language === 'ar' ? 'ar' : 'en';
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  const toggleStep = useCallback((id) => {
    const newSteps = { ...completedSteps, [id]: !completedSteps[id] };
    setCompletedSteps(newSteps);
    localStorage.setItem('pilgrimageProgress', JSON.stringify(newSteps));
  }, [completedSteps]);

  const toggleLanguage = () => {
    const next = language === 'en' ? 'ar' : 'en';
    setLanguage(next);
    localStorage.setItem('language', next);
  };

  const cycleAudience = () => {
    const order = ['all', 'men', 'women'];
    const next = order[(order.indexOf(audience) + 1) % order.length];
    setAudience(next);
    localStorage.setItem('audience', next);
  };

  const toggleMaps = () => {
    const next = !showMaps;
    setShowMaps(next);
    localStorage.setItem('showMaps', String(next));
  };

  const t = UI[language];
  const isAr = language === 'ar';

  // Determine current steps data based on tabs & modes
  let steps = [];
  if (activeTab === 'travel') {
    steps = pilgrimageData.travel || [];
  } else if (activeTab === 'medina') {
    steps = pilgrimageData.medina;
  } else {
    steps = meccaMode === 'umrah' ? pilgrimageData.umrah : pilgrimageData.hajj;
  }

  // Background images per tab
  const PUBLIC = process.env.PUBLIC_URL || '';
  let defaultBackgroundImage = `${PUBLIC}/assets/mecca.jpg`;
  if (activeTab === 'travel') {
    defaultBackgroundImage = `${PUBLIC}/assets/travel.jpg`;
  } else if (activeTab === 'medina') {
    defaultBackgroundImage = `${PUBLIC}/assets/medina.jpg`;
  } else if (meccaMode === 'hajj') {
    defaultBackgroundImage = `${PUBLIC}/assets/hajj.jpg`;
  }

  const backgroundImage = activeStepImage ? `${PUBLIC}${activeStepImage}` : defaultBackgroundImage;

  const handleModeSwitch = () => {
    setMeccaMode(prev => prev === 'umrah' ? 'hajj' : 'umrah');
    setActiveStepImage(null);
  };

  const audienceLabel = {
    all: t.audienceAll,
    men: t.audienceMen,
    women: t.audienceWomen
  }[audience];

  const audienceColor = {
    all: 'bg-white/10',
    men: 'bg-blue-500/30 border-blue-300/50',
    women: 'bg-pink-500/30 border-pink-300/50'
  }[audience];

  return (
    <div className="min-h-screen text-foreground pb-24 relative selection:bg-secondary/30 selection:text-foreground">
      <AnimatedBackground backgroundImage={backgroundImage} />

      {/* Header */}
      <header className="sticky top-0 z-30 bg-black/40 backdrop-blur-xl border-b border-white/10 shadow-lg supports-[backdrop-filter]:bg-black/30">
        <div className="max-w-md mx-auto px-4 py-4 relative">
          {/* Top-right control row: gender filter + maps toggle + language toggle */}
          <div className={`absolute top-3 z-10 flex items-center gap-2 ${isAr ? 'left-3' : 'right-3'}`}>
            <button
              onClick={cycleAudience}
              data-testid="audience-toggle"
              aria-label="Toggle audience filter"
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full border text-white text-xs font-bold transition-colors backdrop-blur-md ${audienceColor}`}
            >
              <span>{audienceLabel}</span>
            </button>
            <button
              onClick={toggleMaps}
              data-testid="maps-toggle"
              aria-label="Toggle maps"
              title={showMaps ? 'Hide maps' : 'Show maps'}
              className={`flex items-center justify-center w-8 h-8 rounded-full border text-xs font-bold transition-colors backdrop-blur-md ${showMaps ? 'bg-secondary/30 border-secondary/50 text-secondary' : 'bg-white/10 border-white/20 text-white/60'}`}
            >
              {showMaps ? <MapIcon className="w-4 h-4" /> : <MapPinOff className="w-4 h-4" />}
            </button>
            <button
              onClick={toggleLanguage}
              data-testid="lang-toggle"
              aria-label="Toggle language"
              className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs font-bold transition-colors backdrop-blur-md"
            >
              <Languages className="w-3.5 h-3.5" />
              <span>{isAr ? t.languageToggleEn : t.languageToggleAr}</span>
            </button>
          </div>

          <h1 className="text-2xl font-bold text-center text-white mb-1 font-arabic drop-shadow-lg tracking-wider mt-8" dir={isAr ? 'rtl' : 'ltr'}>
            {t.title}
          </h1>
          {/* Watermark — under title, smaller, bilingual */}
          <div className="flex items-center justify-center gap-2 mb-1 select-none">
            <span className="text-[11px] text-white/80 font-bold italic tracking-wider whitespace-nowrap" style={{ fontFamily: "'Amiri', serif" }}>
              Made by Rasool Mustafa
            </span>
            <span className="text-white/60 text-xs leading-none">•</span>
            <span className="text-xs text-white/80 font-bold whitespace-nowrap font-arabic" dir="rtl">
              إعداد رسول مصطفى
            </span>
          </div>
          {/* Inquiries email */}
          <div className="text-center mb-4">
            <a href="mailto:RasoolMustafa@yahoo.com" className="text-[10px] text-white/55 hover:text-white/90 transition-colors underline-offset-2 hover:underline">
              {isAr ? 'للاستفسارات: RasoolMustafa@yahoo.com' : 'For inquiries: RasoolMustafa@yahoo.com'}
            </a>
          </div>
          <div className="flex bg-black/40 rounded-full p-1.5 relative shadow-inner border border-white/10">
            {['travel', 'mecca', 'medina'].map((tab) => (
              <button
                key={tab}
                data-testid={`tab-${tab}`}
                onClick={() => {
                  setActiveTab(tab);
                  setActiveStepImage(null);
                }}
                className={`flex-1 relative py-2.5 rounded-full text-sm font-bold capitalize z-10 transition-colors duration-300 ${activeTab === tab ? 'text-black shadow-md' : 'text-gray-300 hover:text-white'}`}
              >
                {activeTab === tab && (
                  <motion.div
                    layoutId="active-tab"
                    className="absolute inset-0 bg-secondary rounded-full -z-10"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
                <span className="flex justify-center items-center gap-1.5">
                  {tab === 'travel' ? <Plane className="w-4 h-4" /> : <MapPin className="w-4 h-4" />}
                  {t[tab]}
                </span>
              </button>
            ))}
          </div>

          {/* Hajj / Umrah Toggle for Mecca Tab */}
          <AnimatePresence>
            {activeTab === 'mecca' && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="mt-4 flex justify-center overflow-hidden"
              >
                <button
                  onClick={handleModeSwitch}
                  className="flex items-center gap-2 px-6 py-2 bg-primary/80 hover:bg-primary backdrop-blur-md rounded-full text-white font-bold text-sm transition-all border border-white/20 shadow-[0_0_15px_rgba(16,185,129,0.3)] hover:scale-105"
                >
                  <RefreshCw className={`w-4 h-4 transition-transform duration-500 ${meccaMode === 'hajj' ? 'rotate-180' : ''}`} />
                  {meccaMode === 'umrah' ? t.switchToHajj : t.switchToUmrah}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 pt-6 pb-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab + meccaMode + language + audience}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.4 }}
            className="space-y-5 relative z-10"
          >
            {steps.map((step, index) => (
              <StepCard
                key={step.id}
                step={step}
                index={index}
                isCompleted={completedSteps[step.id]}
                onToggle={() => toggleStep(step.id)}
                onExpand={(isExpanded) => {
                  if (isExpanded && step.bgImage) {
                    setActiveStepImage(step.bgImage);
                  } else if (!isExpanded) {
                    setActiveStepImage(null);
                  }
                }}
                language={language}
                audience={audience}
                showMaps={showMaps}
                t={t}
              />
            ))}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Salafi sources footer */}
      <footer className="max-w-md mx-auto px-4 pb-24 pt-2" dir={isAr ? 'rtl' : 'ltr'}>
        <div className="border-t border-white/15 pt-4 text-center space-y-2 backdrop-blur-md bg-black/30 rounded-xl p-4 border border-white/10">
          <h5 className="text-xs font-bold text-secondary tracking-wide">
            {isAr ? 'المصادر' : 'Sources & Verification'}
          </h5>
          <p className="text-[11px] text-white/70 leading-relaxed">
            {isAr
              ? 'هذا الدليل مستندٌ إلى منهج السلف الصالح. المصادر الرئيسية: القرآن الكريم (رواية حفص)، والأحاديث الصحيحة من sunnah.com (صحيح البخاري، صحيح مسلم، سنن أبي داود، جامع الترمذي، سنن ابن ماجه)، وكتاب «الحج والعمرة على ضوء الكتاب والسنة» (Salafi Publications).'
              : 'This guide follows authentic Salafi-aligned scholarship. Primary sources: the Qur\'ān (Ḥafs riwāyah), authentic ḥadīth via sunnah.com (Saḥīḥ al-Bukhārī, Saḥīḥ Muslim, Sunan Abī Dāwūd, Jāmi\' at-Tirmidhī, Sunan Ibn Mājah), and "Hajj and Umrah — The Salafi Way" (Salafi Publications).'}
          </p>
          <p className="text-[10px] text-white/50 italic">
            {isAr ? 'وفي حال الإشكال، استشر عالمًا موثوقًا.' : 'In doubt, consult a knowledgeable scholar.'}
          </p>
        </div>
      </footer>

      <NotesFAB language={language} />
    </div>
  );
}

function StepCard({ step, index, isCompleted, onToggle, onExpand, language, audience, showMaps, t }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isNotesExpanded, setIsNotesExpanded] = useState(false);
  const [personalNote, setPersonalNote] = useState("");
  const isAr = language === 'ar';

  useEffect(() => {
    const savedNote = localStorage.getItem(`note_${step.id}`);
    if (savedNote) setPersonalNote(savedNote);
  }, [step.id]);

  const saveNote = (e) => {
    const val = e.target.value;
    setPersonalNote(val);
    localStorage.setItem(`note_${step.id}`, val);
  };

  const title = f(step, 'title', language);
  const subtitle = f(step, 'subtitle', language);
  const definition = f(step, 'definition', language);
  const rawBullets = f(step, 'bullets', language) || [];
  const allBullets = rawBullets.map((b) => normalizeBullet(b, language));
  const bullets = allBullets.filter(b => audience === 'all' || b.audience === 'all' || b.audience === audience);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className={`rounded-2xl overflow-hidden transition-all duration-300 border backdrop-blur-md shadow-xl
        ${isCompleted
          ? 'bg-black/20 border-primary/30 text-gray-400'
          : 'bg-black/30 border-white/20 text-white hover:bg-black/40 hover:border-white/30'}`}
    >
      <div
        className="p-5 flex items-start gap-4 cursor-pointer select-none group"
        onClick={() => {
          const newState = !isExpanded;
          setIsExpanded(newState);
          onExpand(newState);
          if(!newState) setIsNotesExpanded(false);
        }}
        data-testid={`step-${step.id}`}
      >
        <button
          className="mt-1 focus:outline-none"
          onClick={(e) => { e.stopPropagation(); onToggle(); }}
          data-testid={`toggle-complete-${step.id}`}
        >
          {isCompleted ? <CheckCircle2 className="w-7 h-7 text-primary drop-shadow-[0_0_8px_rgba(16,185,129,0.8)]" /> : <Circle className="w-7 h-7 text-white/50 group-hover:text-white transition-colors" />}
        </button>
        <div className="flex-1" dir={isAr ? 'rtl' : 'ltr'}>
          <h3 className={`font-bold text-lg leading-tight ${isCompleted ? 'line-through decoration-1 text-gray-500' : 'text-white'}`}>
            {title}
          </h3>
          {subtitle && <p className={`text-sm mt-1 leading-snug ${isCompleted ? 'text-gray-500' : 'text-gray-300'}`}>{subtitle}</p>}
        </div>
        <div className="flex items-center gap-2">
          {personalNote && !isExpanded && (
            <Edit3 className="w-4 h-4 text-secondary/70" />
          )}
          <ChevronDown className={`w-5 h-5 transition-transform duration-300 mt-1 ${isCompleted ? 'text-gray-500' : 'text-gray-400'} ${isExpanded ? 'rotate-180 text-white' : ''}`} />
        </div>
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="p-5 pt-0 border-t border-white/10 space-y-5" dir={isAr ? 'rtl' : 'ltr'}>

              {/* Mini Map (if step has location data and maps are enabled) */}
              {showMaps && step.map && <MiniMap map={step.map} language={language} />}

              {/* Definition Box */}
              {definition && (
                <div className="bg-amber-500/20 text-amber-50 p-3.5 rounded-xl border border-amber-500/50 text-sm font-medium backdrop-blur-md">
                  <span className="font-bold text-amber-300">{t.term} </span> {definition}
                </div>
              )}

              {/* Bullets — with optional gender chip per bullet */}
              <ul className="space-y-3 text-[15px] text-gray-200">
                {bullets.map((bullet, i) => (
                  <li key={i} className="flex gap-3 leading-relaxed items-start">
                    <span className="text-secondary font-bold mt-1 scale-150 leading-none">•</span>
                    <span className="flex-1">
                      {bullet.audience && bullet.audience !== 'all' && (
                        <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded mr-1.5 align-baseline ${
                          bullet.audience === 'men'
                            ? 'bg-blue-500/30 text-blue-100 border border-blue-300/40'
                            : 'bg-pink-500/30 text-pink-100 border border-pink-300/40'
                        }`}>
                          {bullet.audience === 'men' ? t.forMen : t.forWomen}
                        </span>
                      )}
                      {bullet.text}
                    </span>
                  </li>
                ))}
              </ul>

              {/* Main Dua */}
              {step.dua && (
                <div className="bg-primary/20 rounded-xl p-5 border border-primary/50 shadow-inner space-y-4 backdrop-blur-md">
                  <div className="text-right font-arabic text-3xl leading-[2.5] text-white drop-shadow-md" dir="rtl">
                    {step.dua.arabic}
                  </div>
                  {!isAr && step.dua.english && (
                    <div className="text-[15px] text-gray-100 font-medium border-l-4 border-primary pl-3 py-1">
                      {step.dua.english}
                    </div>
                  )}
                  {step.dua.source && (
                    <div className={`flex items-center justify-between text-xs font-semibold mt-4 pt-3 border-t border-primary/30 ${isAr ? 'flex-row-reverse' : ''}`}>
                      <a href={step.dua.link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-emerald-300 hover:text-white transition-colors">
                        <span>{f(step.dua, 'source', language)}</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                      <span className="text-primary uppercase tracking-wider text-[10px] font-bold bg-primary/20 px-2 py-0.5 rounded-full border border-primary/30">{t.sahih}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Extra Info / Dropdown */}
              <ExtraInfo step={step} language={language} t={t} />

              {/* Personal Notes Section */}
              <div className="mt-6 pt-4 border-t border-white/10">
                <button
                  onClick={() => setIsNotesExpanded(!isNotesExpanded)}
                  className="flex items-center justify-between w-full text-sm font-bold text-secondary hover:text-secondary-foreground transition-colors focus:outline-none"
                >
                  <span className="flex items-center gap-2">
                    <Edit3 className="w-4 h-4" /> {t.personalNotes}
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isNotesExpanded ? 'rotate-180' : ''}`} />
                </button>

                <AnimatePresence>
                  {isNotesExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden mt-3"
                    >
                      <textarea
                        value={personalNote}
                        onChange={saveNote}
                        placeholder={t.personalNotesPlaceholder}
                        className="w-full h-32 bg-black/40 text-white placeholder-gray-500 p-3 rounded-lg border border-white/20 focus:ring-1 focus:ring-secondary focus:border-secondary focus:outline-none resize-none text-sm leading-relaxed"
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function ExtraInfo({ step, language, t }) {
  const [showMore, setShowMore] = useState(false);
  const isAr = language === 'ar';

  const extraInfo = f(step, 'extraInfo', language);
  const factText = f(step.fact, 'text', language);
  const factSource = f(step.fact, 'source', language);
  const quranEnglish = f(step.quran, 'english', language);
  const quranSource = f(step.quran, 'source', language);
  const extraDuaEnglish = f(step.extraDua, 'english', language);
  const extraDuaContext = f(step.extraDua, 'context', language);
  const extraDuaSource = f(step.extraDua, 'source', language);

  if (!extraInfo && !step.quran && !step.extraDua && !step.fact) return null;

  return (
    <div className="mt-6">
      <button
        onClick={() => setShowMore(!showMore)}
        className="w-full py-3.5 bg-white/10 hover:bg-white/20 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-colors text-white border border-white/20 shadow-lg focus:outline-none"
      >
        <BookOpen className="w-4 h-4 text-secondary" />
        {showMore ? t.hideContext : t.moreInfo}
      </button>

      <AnimatePresence>
        {showMore && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden mt-4"
          >
            {/* Fact Box */}
            {step.fact && (
              <div className="bg-blue-500/20 p-4 rounded-xl border border-blue-500/50 mb-4 backdrop-blur-md">
                <h4 className="font-bold text-blue-300 text-sm mb-2 flex items-center gap-2">
                  <Info className="w-4 h-4" /> {t.historicalFact}
                </h4>
                <p className="text-[14px] text-blue-50 mb-3 leading-relaxed">{factText}</p>
                <a href={step.fact.link} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-blue-300 hover:text-blue-100 flex items-center gap-1">
                  {t.sourceLabel} {factSource} <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            )}

            {/* Extra Info Text */}
            {extraInfo && (
              <p className="text-[15px] leading-relaxed text-gray-100 mb-4 bg-black/50 p-4 rounded-xl border border-white/20 shadow-inner whitespace-pre-line">
                {extraInfo}
              </p>
            )}

            {/* Quranic Citation */}
            {step.quran && (
              <div className="bg-emerald-500/20 rounded-xl p-5 border border-emerald-500/50 mb-4 backdrop-blur-md">
                <h4 className="font-bold text-emerald-300 text-sm mb-4 border-b border-emerald-500/30 pb-2">{t.quranicCitation}</h4>
                <div className="text-right font-arabic text-2xl leading-[2.5] text-white drop-shadow-md" dir="rtl">
                  {step.quran.arabic}
                </div>
                {!isAr && quranEnglish && (
                  <div className="text-sm font-medium mt-4 border-l-4 border-emerald-400 pl-3 text-emerald-50">
                    {quranEnglish}
                  </div>
                )}
                <div className={`text-xs font-bold mt-4 text-emerald-300 ${isAr ? 'text-left' : 'text-right'}`}>
                  {quranSource}
                </div>
              </div>
            )}

            {/* Extra Dua with Context */}
            {step.extraDua && (
              <div className="bg-primary/20 rounded-xl p-5 border border-primary/50 shadow-inner space-y-4 backdrop-blur-md">
                <h4 className="font-bold text-primary text-sm mb-2 border-b border-primary/30 pb-2">{t.additionalSupplication}</h4>
                <div className="text-right font-arabic text-3xl leading-[2.5] text-white drop-shadow-md" dir="rtl">
                  {step.extraDua.arabic}
                </div>
                {!isAr && extraDuaEnglish && (
                  <div className="text-[15px] text-gray-100 font-medium border-l-4 border-primary pl-3 py-1">
                    {extraDuaEnglish}
                  </div>
                )}
                {extraDuaContext && (
                  <p className="text-xs text-gray-300 italic pt-2">{extraDuaContext}</p>
                )}
                {extraDuaSource && (
                  <div className={`flex items-center justify-between text-xs font-semibold mt-4 pt-3 border-t border-primary/30 ${isAr ? 'flex-row-reverse' : ''}`}>
                    <a href={step.extraDua.link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-emerald-300 hover:text-white transition-colors">
                      <span>{extraDuaSource}</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;
